import React, { useEffect, useState } from "react";
import axios from "axios";
import { BASE_URL } from "../Apis/apiConfig";
import { Trash2, Edit, Save, ArrowLeft } from "lucide-react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import { useNavigate } from "react-router-dom";

const ManageRules = () => {
  const [rules, setRules] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const navigate = useNavigate();

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  // ✅ GET RULES
  const fetchRules = async () => {
    try {
      const token = localStorage.getItem("token");
      const societyName = localStorage.getItem("society");

      const formData = new FormData();
      formData.append("societyName", societyName);

      const res = await axios.post(
        `${BASE_URL}/api/rules/get-rules-by-admin`,
        formData,
        {
          headers: { Authorization: token ? `Bearer ${token}` : "" },
        }
      );

      setRules(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  // ✅ DELETE
 const handleDelete = async (id) => {
  try {
    const token = localStorage.getItem("token");

    await axios.delete(
      `${BASE_URL}/api/rules/delete-rule-by-admin`,
      {
        params: { ruleId: id }, // ✅ send as query param
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
      }
    );

    // ✅ Update UI
    setRules(rules.filter((rule) => rule.rulesId !== id));

  } catch (err) {
    console.error(err);
    alert("Delete failed");
  }
};

  // ✅ EDIT
  const handleChange = (id, field, value) => {
    const updated = rules.map((r) =>
      r.rulesId === id ? { ...r, [field]: value } : r
    );
    setRules(updated);
  };

  // ✅ UPDATE
const handleUpdate = async (rule) => {
  try {
    const token = localStorage.getItem("token");

    const formData = new FormData();

    formData.append("ruleId", rule.rulesId);
    formData.append("societyName", rule.societyName);

    if (rule.ruleHeader) {
      formData.append("ruleHeader", rule.ruleHeader);
    }

    if (rule.text !== undefined) {
      formData.append("rule", rule.text);
    }

    // ✅ Important: send file only if exists
    if (rule.newImage instanceof File) {
      formData.append("ruleImage", rule.newImage);
    }

    if (rule.newPdf instanceof File) {
      formData.append("rulePdf", rule.newPdf);
    }

    await axios.post(
      `${BASE_URL}/api/rules/edit-rule-by-admin`,
      formData,
      {
        headers: {
          Authorization: token ? `Bearer ${token}` : "", // ✅ FIXED (important)
          // ❌ DO NOT manually set Content-Type (axios handles it)
        },
      }
    );

    setEditingId(null);
    alert("Updated successfully");

    // ✅ Refresh data after update
    fetchRules();

  } catch (err) {
    console.error("UPDATE ERROR:", err.response?.data || err.message);
    alert("Update failed");
  }
};

  // ✅ BASE64 HANDLER (your API already sends base64)
  const getImageSrc = (img) => img ? `data:image/jpeg;base64,${img}` : null;
  const getPdfSrc = (pdf) => pdf ? `data:application/pdf;base64,${pdf}` : null;

  return (
    <div style={styles.appContainer}>
      <Header toggleSidebar={toggleSidebar} />

      <div style={styles.mainLayout}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

        <main style={styles.contentArea}>
          <SubHeader />

          <div style={styles.container}>

            {/* ✅ BACK BUTTON */}
            <button
              onClick={() => navigate(-1)}
              style={styles.backBtn}
            >
              <ArrowLeft size={18} /> Back
            </button>

            {rules.map((rule, index) => (
              <div key={rule.rulesId} style={styles.ruleCard}>

                {/* HEADER */}
                {editingId === rule.rulesId ? (
                  <input
                    value={rule.ruleHeader}
                    onChange={(e) =>
                      handleChange(rule.rulesId, "ruleHeader", e.target.value)
                    }
                    style={styles.input}
                  />
                ) : (
                  <h3 style={styles.ruleHeader}>
                    <span style={styles.ruleNumber}>{index + 1}.</span>{" "}
                    {rule.ruleHeader}
                  </h3>
                )}

                {/* TEXT */}
                {editingId === rule.rulesId ? (
                  <textarea
                    value={rule.text || ""}
                    onChange={(e) =>
                      handleChange(rule.rulesId, "text", e.target.value)
                    }
                    style={styles.textarea}
                  />
                ) : (
                  <p style={styles.ruleText}>
                    {rule.text || "No Description"}
                  </p>
                )}

                {/* ✅ FILE SECTION */}
                <div style={styles.fileSection}>

                  {/* IMAGE */}
                  {rule.image ? (
                    <button
                      onClick={() => setSelectedImage(getImageSrc(rule.image))}
                      style={styles.viewBtn}
                    >
                      🖼 View Image
                    </button>
                  ) : (
                    <span style={styles.emptyText}>No Image</span>
                  )}

                  {/* PDF */}
                  {rule.pdf ? (
                    <a
                      href={getPdfSrc(rule.pdf)}
                      download={`rule_${rule.rulesId}.pdf`}
                      style={styles.downloadBtn}
                    >
                      📄 Download PDF
                    </a>
                  ) : (
                    <span style={styles.emptyText}>No PDF</span>
                  )}

                </div>
                <div style={styles.actions}>
                  {editingId === rule.rulesId ? (
                    <button style={styles.saveBtn} onClick={() => handleUpdate(rule)}>
                      <Save size={16} /> Save
                    </button>
                  ) : (
                    <button
                      style={styles.editBtn}
                      onClick={() => setEditingId(rule.rulesId)}
                    >
                      <Edit size={16} /> Edit
                    </button>
                  )}

                  <button
                    style={styles.deleteBtn}
                    onClick={() => handleDelete(rule.rulesId)}
                  >
                    <Trash2 size={16} /> Delete
                  </button>
                </div>

              </div>
            ))}
          </div>
        </main>
      </div>

      {selectedImage && (
        <div style={styles.modal} onClick={() => setSelectedImage(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <img src={selectedImage} alt="preview" style={styles.modalImg} />
            <button onClick={() => setSelectedImage(null)} style={styles.closeBtn}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageRules;

const styles = {
  appContainer: {
    fontFamily: "Inter, sans-serif",
    backgroundColor: "#f8fafc",
    height: "100vh",         
    overflow: "hidden",      
  },

  mainLayout: { 
    display: "flex",
    height: "100%",          
  },

  contentArea: { 
    flex: 1, 
    padding: "20px 30px",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",       
  },

  container: { 
    maxWidth: "900px", 
    margin: "0 auto",
    overflowY: "auto",       
    flex: 1,                 
    paddingRight: "10px",  
     paddingBottom: "40px" 
  },

  backBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 14px",
    backgroundColor: "#4f46e5",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    marginBottom: "20px",
    position: "sticky",       // ✅ stays visible
    top: 0,
    zIndex: 10
  },

  ruleCard: {
    background: "#fff",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "15px",
    boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
  },

  ruleHeader: { fontSize: "18px", fontWeight: "600" },
  ruleNumber: { color: "#0ea5e9" },
  ruleText: { color: "#475569" },

  fileSection: {
    marginTop: "10px",
    display: "flex",
    gap: "10px",
    flexWrap: "wrap"
  },

  viewBtn: {
    backgroundColor: "#0ea5e9",
    color: "#fff",
    border: "none",
    padding: "6px 12px",
    borderRadius: "6px",
    cursor: "pointer"
  },

  downloadBtn: {
    backgroundColor: "#16a34a",
    color: "#fff",
    padding: "6px 12px",
    borderRadius: "6px",
    textDecoration: "none"
  },

  emptyText: { color: "#94a3b8" },

  actions: { marginTop: "12px", display: "flex", gap: "10px" },

  editBtn: { backgroundColor: "#0ea5e9", color: "#fff", border: "none", padding: "8px", borderRadius: "6px" },
  saveBtn: { backgroundColor: "#16a34a", color: "#fff", border: "none", padding: "8px", borderRadius: "6px" },
  deleteBtn: { backgroundColor: "#ef4444", color: "#fff", border: "none", padding: "8px", borderRadius: "6px" },

  modal: {
    position: "fixed",
    top: 0, left: 0,
    width: "100%", height: "100%",
    backgroundColor: "rgba(0,0,0,0.7)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },

  modalContent: {
    background: "#fff",
    padding: "15px",
    borderRadius: "10px"
  },

  modalImg: {
    maxWidth: "100%",
    maxHeight: "70vh"
  },

  closeBtn: {
    marginTop: "10px",
    padding: "6px 12px",
    backgroundColor: "#ef4444",
    color: "#fff",
    border: "none",
    borderRadius: "6px"
  }
};